/** A query error with a zero-based offset into the original input. */
export class SearchQueryError extends Error {
  constructor(
    message: string,
    public readonly position: number
  ) {
    super(message);
    this.name = 'SearchQueryError';
  }
}

type DateField = 'created' | 'updated';
type DateValue =
  | { kind: 'range'; start?: string; end?: string }
  | { kind: 'rolling'; amount: number; unit: 'd' | 'w' | 'm' | 'y' };

export type SearchExpression =
  | { type: 'all' }
  | { type: 'text'; key: string; match: string }
  | { type: 'date'; field: DateField; value: DateValue; position: number }
  | { type: 'not'; operand: SearchExpression }
  | { type: 'and'; left: SearchExpression; right: SearchExpression }
  | { type: 'or'; left: SearchExpression; right: SearchExpression };

type Token = { value: string; position: number; quoted?: boolean };

function validDate(value: string, position: number): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new SearchQueryError('Expected an ISO date (YYYY-MM-DD)', position);
  }
  const date = new Date(value + 'T00:00:00Z');
  if (
    !Number.isFinite(date.getTime()) ||
    date.toISOString().slice(0, 10) !== value
  ) {
    throw new SearchQueryError('Invalid calendar date', position);
  }
  return value;
}

function dateValue(raw: string, position: number): DateValue {
  const rolling = /^(\d+)([dwmy])$/i.exec(raw);
  if (rolling) {
    const amount = Number(rolling[1]);
    if (!Number.isSafeInteger(amount) || amount <= 0 || amount > 3650000) {
      throw new SearchQueryError('Invalid rolling duration', position);
    }
    return {
      kind: 'rolling',
      amount,
      unit: rolling[2].toLowerCase() as 'd' | 'w' | 'm' | 'y',
    };
  }
  if (raw.includes('..')) {
    const parts = raw.split('..');
    if (parts.length !== 2 || (!parts[0] && !parts[1])) {
      throw new SearchQueryError('Invalid date range', position);
    }
    const start = parts[0] ? validDate(parts[0], position) : undefined;
    const end = parts[1]
      ? validDate(parts[1], position + parts[0].length + 2)
      : undefined;
    if (start && end && start > end)
      throw new SearchQueryError('Date range is reversed', position);
    return { kind: 'range', start, end };
  }
  const day = validDate(raw, position);
  return { kind: 'range', start: day, end: day };
}

/**
 * Parse AND/OR, implicit AND and !, in descending precedence: !, AND, OR.
 * Queries are limited to 4096 UTF-16 code units, 512 tokens, and 64 nested
 * groups/negations so untrusted API input cannot exhaust the call stack.
 */
export function parseSearchQuery(input: string): SearchExpression {
  if (input.length > 4096)
    throw new SearchQueryError('Query exceeds 4096 characters', 4096);
  const tokens: Token[] = [];
  for (let i = 0; i < input.length;) {
    if (/\s/.test(input[i])) {
      i++;
      continue;
    }
    const position = i;
    if (tokens.length >= 512)
      throw new SearchQueryError('Query exceeds 512 tokens', position);
    if ('()!'.includes(input[i])) {
      tokens.push({ value: input[i++], position });
      continue;
    }
    if (input[i] === '"') {
      i++;
      let value = '"';
      let closed = false;
      while (i < input.length) {
        const char = input[i++];
        value += char;
        if (char === '\\' && i < input.length) value += input[i++];
        else if (char === '"') {
          closed = true;
          break;
        }
      }
      if (!closed)
        throw new SearchQueryError('Unterminated quoted phrase', position);
      if (!value.slice(1, -1).trim())
        throw new SearchQueryError('Empty quoted phrase', position);
      tokens.push({ value, position, quoted: true });
      continue;
    }
    while (i < input.length && !/[\s()!"]/.test(input[i])) i++;
    tokens.push({ value: input.slice(position, i), position });
  }
  let cursor = 0;
  const is = (word: string) =>
    !tokens[cursor]?.quoted && tokens[cursor]?.value.toUpperCase() === word;
  let depth = 0;
  function primary(): SearchExpression {
    if (depth >= 64)
      throw new SearchQueryError(
        'Query exceeds 64 nesting levels',
        tokens[cursor]?.position ?? input.length
      );
    depth++;
    try {
      return readPrimary();
    } finally {
      depth--;
    }
  }
  function readPrimary(): SearchExpression {
    const token = tokens[cursor++];
    if (!token) throw new SearchQueryError('Expected an operand', input.length);
    if (!token.quoted && token.value === '!')
      return { type: 'not', operand: primary() };
    if (!token.quoted && token.value === '(') {
      const expression = or();
      if (!is(')'))
        throw new SearchQueryError(
          'Expected closing parenthesis',
          tokens[cursor]?.position ?? input.length
        );
      cursor++;
      return expression;
    }
    if (
      !token.quoted &&
      [')', 'AND', 'OR'].includes(token.value.toUpperCase())
    ) {
      throw new SearchQueryError('Expected an operand', token.position);
    }
    const field = /^(created|updated):/i.exec(token.value);
    if (!token.quoted && field) {
      return {
        type: 'date',
        field: field[1].toLowerCase() as DateField,
        value: dateValue(
          token.value.slice(field[0].length),
          token.position + field[0].length
        ),
        position: token.position + field[0].length,
      };
    }
    return { type: 'text', key: token.value, match: token.value };
  }
  function and(): SearchExpression {
    let left = primary();
    while (cursor < tokens.length && !is('OR') && !is(')')) {
      if (is('AND')) cursor++;
      left = { type: 'and', left, right: primary() };
    }
    return left;
  }
  function or(): SearchExpression {
    let left = and();
    while (is('OR')) {
      cursor++;
      left = { type: 'or', left, right: and() };
    }
    return left;
  }
  if (!tokens.length) return { type: 'all' };
  const expression = or();
  if (cursor !== tokens.length)
    throw new SearchQueryError(
      'Unexpected closing parenthesis',
      tokens[cursor].position
    );
  return expression;
}

/** Collect indexer requests; a key is positive if any occurrence is positive. */
export function textOperands(
  expression: SearchExpression
): { key: string; match: string; positive: boolean }[] {
  const operands = new Map<
    string,
    { key: string; match: string; positive: boolean }
  >();
  function visit(node: SearchExpression, positive: boolean, depth = 0) {
    if (depth > 512)
      throw new SearchQueryError('Expression exceeds 512 nesting levels', 0);
    if (node.type === 'text') {
      const existing = operands.get(node.key);
      operands.set(node.key, {
        key: node.key,
        match: node.match,
        positive: positive || !!existing?.positive,
      });
    } else if (node.type === 'not') visit(node.operand, !positive, depth + 1);
    else if (node.type === 'and' || node.type === 'or') {
      visit(node.left, positive, depth + 1);
      visit(node.right, positive, depth + 1);
    }
  }
  visit(expression, true);
  return [...operands.values()];
}

type LocalTime = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
};
function localTime(
  timestamp: number,
  formatter: Intl.DateTimeFormat
): LocalTime {
  const parts = Object.fromEntries(
    formatter
      .formatToParts(timestamp)
      .map(part => [part.type, Number(part.value)])
  );
  const era = formatter
    .formatToParts(timestamp)
    .find(part => part.type === 'era')?.value;
  return {
    year: era === 'BC' ? 1 - parts.year : parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second,
    millisecond: ((timestamp % 1000) + 1000) % 1000,
  };
}
function utc(time: LocalTime): number {
  const date = new Date(0);
  date.setUTCFullYear(time.year, time.month - 1, time.day);
  date.setUTCHours(time.hour, time.minute, time.second, time.millisecond);
  return date.getTime();
}
function instant(time: LocalTime, formatter: Intl.DateTimeFormat): number {
  const target = utc(time);
  if (!Number.isFinite(target)) return NaN;
  // Resolve offsets on both sides of transitions. In a fold choose the earlier
  // instant; in a gap advance by the gap (Temporal's compatible convention).
  const candidates = [-2, -1, 0, 1, 2].map(days => {
    const probe = target + days * 86400000;
    return target - (utc(localTime(probe, formatter)) - probe);
  });
  const exact = candidates.filter(
    candidate => utc(localTime(candidate, formatter)) === target
  );
  if (exact.length) return Math.min(...exact);
  return candidates
    .filter(candidate => utc(localTime(candidate, formatter)) > target)
    .sort((a, b) => a - b)[0];
}
function shift(
  time: LocalTime,
  amount: number,
  unit: 'd' | 'w' | 'm' | 'y'
): LocalTime {
  const date = new Date(utc(time));
  if (unit === 'd' || unit === 'w')
    date.setUTCDate(date.getUTCDate() - amount * (unit === 'w' ? 7 : 1));
  else {
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() - amount * (unit === 'y' ? 12 : 1));
    const last = new Date(date.getTime());
    last.setUTCMonth(last.getUTCMonth() + 1, 0);
    date.setUTCDate(Math.min(time.day, last.getUTCDate()));
  }
  return {
    ...time,
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
}

/** Evaluate exclusively within the supplied universe; missing dates never match. */
export function evaluateSearchQuery(
  expression: SearchExpression,
  universe: Iterable<string>,
  textMatches: Map<string, Set<string>>,
  dates: Map<string, { created?: number; updated?: number }>,
  options: { now?: number; timezone?: string } = {}
): Set<string> {
  const now = options.now ?? Date.now();
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: options.timezone ?? 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    era: 'short',
  });
  const predicates = new Map<SearchExpression, (id: string) => boolean>();
  function compile(node: SearchExpression, depth = 0): (id: string) => boolean {
    if (depth > 512)
      throw new SearchQueryError('Expression exceeds 512 nesting levels', 0);
    const cached = predicates.get(node);
    if (cached) return cached;
    let predicate: (id: string) => boolean;
    if (node.type === 'all') predicate = () => true;
    else if (node.type === 'text')
      predicate = id => textMatches.get(node.key)?.has(id) ?? false;
    else if (node.type === 'not') {
      const operand = compile(node.operand, depth + 1);
      predicate = id => !operand(id);
    } else if (node.type === 'and' || node.type === 'or') {
      const left = compile(node.left, depth + 1),
        right = compile(node.right, depth + 1);
      predicate =
        node.type === 'and'
          ? id => left(id) && right(id)
          : id => left(id) || right(id);
    } else {
      const value = node.value;
      let start = -Infinity,
        end = Infinity;
      if (value.kind === 'rolling') {
        start = instant(
          shift(localTime(now, formatter), value.amount, value.unit),
          formatter
        );
        end = now;
      } else {
        const midnight = (day: string): LocalTime => {
          const [year, month, date] = day.split('-').map(Number);
          return {
            year,
            month,
            day: date,
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
          };
        };
        if (value.start) start = instant(midnight(value.start), formatter);
        if (value.end)
          end = instant(shift(midnight(value.end), -1, 'd'), formatter) - 1;
      }
      if (
        (!Number.isFinite(start) && start !== -Infinity) ||
        (!Number.isFinite(end) && end !== Infinity)
      )
        throw new SearchQueryError(
          'Date is outside the supported range',
          node.position
        );
      predicate = id => {
        const date = dates.get(id)?.[node.field];
        return (
          date !== undefined &&
          Number.isFinite(date) &&
          date >= start &&
          date <= end
        );
      };
    }
    predicates.set(node, predicate);
    return predicate;
  }
  const matches = compile(expression);
  return new Set([...universe].filter(matches));
}
